import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import _ from 'lodash';

import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import { formatServerDateTime, formatDisplayDate, parseDisplayDate } from '@/util/MomentUtils';
import { validateForm } from '@/util/ParsleyUtils';

import SaveCustomerOrderGeneral from '../../../services/SaveCustomerOrderGeneral';

import tmplPRTNRGeneralView from './PRTNRGeneralView.hbs';

const PRTNRGeneralView = View.extend({
  template: tmplPRTNRGeneralView,

  className: 'prtnr-general-view',

  ui: {
    dateEquipmentReq: 'input[name="dateEquipmentReq"]',
    commandPOC: '#commandPOC',
    generalForm: '.general-form',
    firstName: '#firstName',
    lastName: '#lastName',
    email: '#email',
    telephone: '#telephone',
    baseAccessReqNotes: '.baseAccessReqNotes'
  },

  events: {
    'change @ui.commandPOC': 'onCommandPOCChange'
  },

  onRender() {
    const order = this.model.get('order');

    this.ui.dateEquipmentReq.datepicker({ format: 'mm/dd/yyyy' });
    if (order.dateEquipmentReq) {
      this.ui.dateEquipmentReq.datepicker('setDate', formatDisplayDate(order.dateEquipmentReq));
    }

    order.hasReq = order.hasBaseAccessReq;
    order.accessNotes = order.baseAccessReqNotes;
  },

  onCommandPOCChange() {
    const commandPOCs = this.model.get('orderDetails').commandPOCs;
    const commandId = this.ui.commandPOC.val();

    if (commandId) {
      const command = _.find(commandPOCs, { id: commandId });
      this.$('#firstName').val(`${command.firstName}`);
      this.$('#lastName').val(`${command.lastName}`);
      this.$('#email').val(command.email);
      this.$('#telephone').val(command.telephone);
    } else {
      this.$('#firstName').val('');
      this.$('#lastName').val('');
      this.$('#email').val('');
      this.$('#telephone').val('');
    }
    validateForm(this.ui.generalForm, 'commandPOCInfo');
  },

  async validate() {
    const isReadOnly = this.model.get('isReadOnly');

    try {
      if (isReadOnly) this.ui.generalForm.find(':input').prop('disabled', false);
      await validateForm(this.ui.generalForm);
      this.trigger('no:errors');
      return true;
    } catch {
      if (isReadOnly) {
        this.ui.generalForm.find(':input').prop('disabled', true);
      }
      this.trigger('has:errors');
      return false;
    }
  },

  async onAttach() {
    if (this.model.get('orderDetails').showValidation) {
      await this.validate();
    }
  },

  async onSave() {
    const serialFormData = Syphon.serialize(this);

    if (serialFormData.dateEquipmentReq) {
      const dateEquipmentReq = serialFormData.dateEquipmentReq;
      const isLegacySupport = false;
      serialFormData.numMonthsUntilFYEnd = this.calculateNumMonthsUntilFYEnd(dateEquipmentReq, isLegacySupport);
      serialFormData.dateEquipmentReq = formatServerDateTime(parseDisplayDate(dateEquipmentReq));
    }

    const formData = {
      generalFormData: serialFormData,
      orderId: this.model.get('orderDetails').orderId,
      orderTypeId: this.model.get('orderDetails').orderTypeId
    };

    try {
      await SaveCustomerOrderGeneral(formData);

      Object.assign(this.model.get('order'), {
        ...serialFormData,
        hasReq: serialFormData.hasBaseAccessReq,
        accessNotes: serialFormData.baseAccessReqNotes
      });

      this.showSaveSuccess();
    } catch (err) {
      this.showSaveError();
      throw new Error(err);
    }
  },

  calculateNumMonthsUntilFYEnd(dateEquipmentReq, isLegacySupport) {
    const OCT_1_2025 = new Date(2025, 9, 1);
    const MAR_31_2026 = new Date(2026, 2, 31);

    const [mm, dd, yyyy] = dateEquipmentReq.split('/').map(Number);
    const requestDate = new Date(yyyy, mm - 1, dd);
    const requestMonth = requestDate.getMonth() + 1;
    const requestYear = requestDate.getFullYear();
    const transitionRequestYear = 2027;

    /*  
    Legacy Support:
      OLD: dateEquipmentReq before Oct 1, 2025: Sept 30 cutoff
      NEW: dateEquipmentReq after March 31, 2026: March 31 cutoff
    NOT Legacy Support:
      All orders (past and present) use the March 31 cutoff
    */
    const cutoffMonth = requestDate < OCT_1_2025 && isLegacySupport ? 9 : 3;
    const isTransitionPeriod = requestDate >= OCT_1_2025 && requestDate <= MAR_31_2026;

    /* 
    yearOffset represents how many full calendar years after the request year the fiscal cutoff occurs
    (0 = same year, 1 = next year, 2 = transition year)
    */
    const yearOffset = isTransitionPeriod ? (transitionRequestYear - requestYear) : (requestMonth > cutoffMonth ? 1 : 0);
    const numMonthsUntilFYEnd = (yearOffset * 12) + (cutoffMonth - requestMonth) + 1;

    return numMonthsUntilFYEnd;
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving general information for your order. Try again or contact an administrator if your issue persists.'
    });
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Save Success',
      message: 'This order has been saved successfully.',
      hideAfter: 5
    });
  }
});

export default PRTNRGeneralView;
